import { useQuery } from '@tanstack/react-query';

import {
  useAddViewMutation,
  useDefaultViewMutation,
  useGetViewByIdQuery,
  useGetViewNamesQuery,
  useRemoveViewMutation,
  useUpdateViewMutation,
} from '@/app/services/customViews';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAccessToken } from '@/services/auth';

import {
  AddViewRequest,
  CustomView,
  SetDefaultViewRequest,
  UpdateViewRequest,
  useAddViewRestMutation,
  useRemoveViewRestMutation,
  useSetDefaultViewRestMutation,
  useUpdateViewRestMutation,
  viewByIdOptions,
  viewNamesOptions,
} from './index';

export type {
  AddViewRequest,
  CustomView,
  SetDefaultViewRequest,
  UpdateViewRequest,
};

const FLAG = 'customViewsRest';

export function useGetViewNames(params: {
  username: string;
  usage: string;
  app: string;
}) {
  const isRest = useFeatureFlag(FLAG);
  const token = useAccessToken();
  const restQuery = useQuery({
    ...viewNamesOptions(token, params),
    enabled: !!token && isRest,
  });

  const gqlQuery = useGetViewNamesQuery(
    { username: params.username, usage: params.usage, app: params.app },
    { skip: isRest },
  );

  if (isRest) {
    return {
      data: restQuery.data ?? [],
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }

  return {
    data: (gqlQuery.data?.viewNames ?? []) as CustomView[],
    isLoading: gqlQuery.isLoading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useGetViewById(
  id: string | undefined,
  options?: { enabled?: boolean },
) {
  const isRest = useFeatureFlag(FLAG);
  const token = useAccessToken();
  const canFetch = (options?.enabled ?? true) && !!id;

  const restQuery = useQuery({
    ...viewByIdOptions(token, id ?? ''),
    enabled: !!token && isRest && canFetch,
  });

  const gqlQuery = useGetViewByIdQuery(
    { id: id! },
    { skip: isRest || !canFetch, refetchOnMountOrArgChange: true },
  );

  if (isRest) {
    return {
      data: restQuery.data as CustomView | undefined,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }

  return {
    data: (gqlQuery.data?.viewById ?? undefined) as CustomView | undefined,
    isLoading: gqlQuery.isLoading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useAddView() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useAddViewRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] = useAddViewMutation();

  const mutateAsync = async (
    request: AddViewRequest,
  ): Promise<{ id?: string }> => {
    if (isRest) {
      const view = await restMutation.mutateAsync(request);
      return { id: view.id };
    }
    await gqlMutate({ input: { ...request, app: request.app ?? '' } });
    return {};
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateView() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateViewRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] = useUpdateViewMutation();

  const mutateAsync = async (request: UpdateViewRequest): Promise<void> => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    await gqlMutate({ input: { id: request.id, view: request.view } });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useRemoveView() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useRemoveViewRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] = useRemoveViewMutation();

  const mutateAsync = async (id: string): Promise<void> => {
    if (isRest) {
      return restMutation.mutateAsync(id);
    }
    await gqlMutate({ id });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useSetDefaultView() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useSetDefaultViewRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] = useDefaultViewMutation();

  const mutateAsync = async (request: SetDefaultViewRequest): Promise<void> => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    await gqlMutate({
      input: {
        viewId: request.id,
        username: request.username,
        usage: request.usage,
      },
    });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}
